import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { logAPIError } from '../utils/errorLogger';
import generateAccessoryCode from '../utils/accessoryCodeGenerator';

const accessoryTypes = ['Düğme', 'Fermuar', 'Toka', 'Etiket', 'Astar', 'Diğer'];

interface CreateAccessoryProps {
  onSuccess?: (accessory: any) => void;
  onCancel?: () => void;
  isDrawerMode?: boolean;
}

const CreateAccessory: React.FC<CreateAccessoryProps> = ({
  onSuccess,
  onCancel,
  isDrawerMode = false
}) => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    type: accessoryTypes[0],
    color: '#000000',
    description: '',
  });

  // Dosya yükleme için state'ler
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]); // Yüklenen resimlerin URL'lerini tut
  const [currentlyUploading, setCurrentlyUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Resim temizleme - component unmount olduğunda
  useEffect(() => {
    return () => {
      // Component unmount olduğunda önizleme URL'lerini temizle
      imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [imagePreviews]);

  // Resim yükleme fonksiyonu - resim seçildiğinde çağrılır
  const uploadImage = async (file: File) => {
    setCurrentlyUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    const formData = new FormData();
    formData.append('images', file);
    
    try {
      console.log('Görsel yükleme isteği gönderiliyor...', `${apiUrl}/upload-images`);
      
      const response = await axios.post(
        `${apiUrl}/upload-images`, 
        formData, 
        {
          headers: {'Content-Type': 'multipart/form-data'},
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Yükleme durumu: %' + percentCompleted);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      console.log('Görsel yükleme başarılı:', response.data);
      // Yüklenen resmin URL'sini diziye ekle
      if (response.data && response.data.imageUrls && response.data.imageUrls.length > 0) {
        setUploadedImageUrls(prev => [...prev, ...response.data.imageUrls]);
      }
      setCurrentlyUploading(false);
      setUploadProgress(100);
      
      // Kısa bir süre sonra ilerleme çubuğunu sıfırla
      setTimeout(() => {
        setUploadProgress(0);
      }, 1500);
      
      return response.data.imageUrls;
    } catch (error: any) {
      console.error('Görsel yükleme hatası:', error);
      let errorMsg = 'Bilinmeyen hata';
      
      // Hata detayını kontrol et
      if (error.response) {
        // Sunucudan bir yanıt alındı
        errorMsg = `Sunucu hatası (${error.response.status}): ${error.response.data.error || error.message}`;
        console.error('Sunucu yanıtı:', error.response.data);
      } else if (error.request) {
        // İstek yapıldı ama yanıt alınamadı
        errorMsg = 'Sunucu yanıt vermiyor. Ağ bağlantınızı kontrol edin.';
      } else {
        // İstek yapılırken bir şeyler yanlış gitti
        errorMsg = `Hata: ${error.message}`;
      }
      
      setUploadError(`Görsel yüklenirken hata oluştu: ${errorMsg}`);
      setCurrentlyUploading(false);
      return null;
    }
  };

  // Çoklu resim yükleme
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      
      // Maximum 5 dosya kontrolü
      if (selectedImages.length + filesArray.length > 5) {
        alert('En fazla 5 resim yükleyebilirsiniz.');
        return;
      }
      
      // Her bir dosya için preview oluştur ve yükle
      for (const file of filesArray) {
        // Önizleme oluştur
        const preview = URL.createObjectURL(file);
        setImagePreviews(prev => [...prev, preview]);
        
        // Seçili dosyalara ekle
        setSelectedImages(prev => [...prev, file]);
        
        // Dosyayı sunucuya yükle
        await uploadImage(file);
      }
    }
  };

  // Resim kaldırma
  const handleRemoveImage = async (index: number) => {
    // Önce önizleme ve seçili resimler dizilerini güncelle
    URL.revokeObjectURL(imagePreviews[index]);
    
    // URL'leri güncellemek için - eğer bu resim yüklendiyse
    if (index < uploadedImageUrls.length) {
      setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
    }
    
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Aksesuar oluşturma
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);
    
    // Form validasyonu
    if (!form.name || !form.type) {
      alert('Lütfen gerekli alanları doldurun.');
      return;
    }

    try {
      setLoading(true);
      
      // Aksesuar kodu oluştur
      const accessoryCode = generateAccessoryCode(form.type);
      
      // Aksesuar verisini hazırla
      const accessoryData = {
        name: form.name.trim(),
        type: form.type,
        color: form.color || '#000000',
        description: form.description || '',
        images: uploadedImageUrls, // Yüklenen resimlerin URL'lerini ekle
        code: accessoryCode // Otomatik oluşturulan kodu ekle
      };
      
      console.log('Aksesuar kodu:', accessoryCode);
      console.log('Aksesuar verileri:', accessoryData);
      
      // Aksesuar oluştur
      const response = await axios.post(
        `${apiUrl}/accessories`, 
        accessoryData,
        { headers: { 'Content-Type': 'application/json' }}
      );
      
      console.log('Aksesuar oluşturma yanıtı:', response.data);
      
      // Başarılı olduğunda önizlemeleri temizle
      imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
      setImagePreviews([]);
      setSelectedImages([]);
      setUploadedImageUrls([]);
      
      setLoading(false);

      // Drawer modunda başarı callback'ini çağır
      if (isDrawerMode && onSuccess) {
        onSuccess(response.data);
      } else {
        navigate('/accessories');
      }
    } catch (err) {
      setLoading(false);
      console.error('Aksesuar oluşturma hatası:', err);
      
      if (axios.isAxiosError(err) && err.response) {
        alert(`Aksesuar eklenirken bir hata oluştu: ${err.response.data.error || err.message}`);
      } else {
        alert('Aksesuar eklenirken bir hata oluştu.');
      }
      logAPIError(err, 'Aksesuar oluşturma');
    }
  };

  // Temizleme fonksiyonu - oluşturulabilir ama kullanılmamakta
  const clearAllImages = () => {
    imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    setImagePreviews([]);
    setSelectedImages([]);
    setUploadedImageUrls([]);
  };

  return (
    <div className={`${isDrawerMode ? '' : 'container mx-auto px-4 py-8'}`}>
      {!isDrawerMode && (
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/accessories')}
            className="flex items-center mr-4 text-blue-600 hover:text-blue-800 transition duration-200"
            disabled={loading || currentlyUploading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Geri</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Yeni Aksesuar Ekle</h1>
        </div>
      )}

      {uploadError && (
        <div className="mb-6 bg-yellow-50 border border-yellow-300 p-4 rounded-md">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 0 00-1 1v3a1 1 0 002 0V6a1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-yellow-700">{uploadError}</p>
          </div>
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Temel Bilgiler */}
          <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Temel Bilgiler
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Aksesuar Adı 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  required
                  placeholder="Aksesuar adını girin"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Aksesuar Tipi 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                  required
                >
                  {accessoryTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Renk
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    name="color"
                    value={form.color}
                    onChange={handleChange}
                    className="p-1 border border-gray-300 rounded-md h-10 w-10"
                  />
                  <input
                    type="text"
                    name="color"
                    value={form.color}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Aksesuar ile ilgili notlar..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
              </div>
            </div>
          </div>

          {/* Görseller */}
          <div className="p-4 bg-gray-50/70 rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b border-gray-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              Aksesuar Görselleri
            </h2>
            
            <div>
              <p className="text-sm text-gray-500 mb-4">
                En fazla 5 görsel yükleyebilirsiniz. Görseller ürün kalitesini gösterecek şekilde net olmalıdır.
              </p>
              
              {/* Yükleme durumu */}
              {currentlyUploading && (
                <div className="mb-4">
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-blue-600 font-medium">Görsel yükleniyor...</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Yüklenen görsellerin önizlemesi */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <div className={`w-full h-28 rounded-md overflow-hidden border ${
                      index < uploadedImageUrls.length ? 'border-green-300' : 'border-gray-300'
                    }`}>
                      <img
                        src={preview}
                        alt={`Görsel ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {index < uploadedImageUrls.length && (
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm transition-colors duration-200"
                      title="Görseli kaldır"
                      disabled={currentlyUploading}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                
                {/* Yeni görsel ekleme butonu */}
                {selectedImages.length < 5 && (
                  <div className="w-full h-28">
                    <label className={`flex flex-col items-center justify-center w-full h-full px-4 py-3 bg-white rounded-md shadow-sm border border-gray-300 border-dashed cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                      currentlyUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="mt-2 text-xs text-gray-500">Görsel Ekle</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        className="hidden" 
                        multiple 
                        disabled={currentlyUploading} 
                      />
                    </label>
                  </div>
                )}
              </div>
              
              <div className="text-xs text-gray-500 italic mt-2">
                {selectedImages.length} / 5 görsel yüklendi 
                {uploadedImageUrls.length > 0 && 
                  ` (${uploadedImageUrls.length} görsel sunucuya yüklendi)`
                }
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-8 space-x-2 border-t pt-6">
          <button
            type="button"
            onClick={onCancel || (() => navigate('/accessories'))}
            className="px-5 py-2.5 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading || currentlyUploading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            İptal
          </button>
          <button
            type="submit"
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading || currentlyUploading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kaydediliyor...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAccessory;
